import { useUpdateAccountLanguage } from '@application/api/account/useUpdateAccountLanguage';
import {
  COOKIE_KEY_PREFERRED_COUNTRY,
  COOKIE_KEY_PREFERRED_LANGUAGE,
} from '@application/constants';
import { i18n } from '@application/i18n';
import { useTranslatedUrlState } from '@application/state';
import type { Country, Language } from '@application/types';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useRouter } from '.';
import { cookies } from '..';

export const useChangeLocale = () => {
  const translatedUrlState = useTranslatedUrlState();
  const { updateAccountLanguage } = useUpdateAccountLanguage();
  const { language, country, infoFromCurrentPath, navigateToRoute } = useRouter();

  const changeLocale = useCallback(
    async (
      newLanguage: Language | undefined,
      newCountry: Country | undefined,
      shouldUpdateAccount = false,
    ) => {
      if (newLanguage === language && newCountry === country) return;
      const languageToChangeTo = newLanguage || language;
      const countryToChangeTo = newCountry || country;

      // We reload resources of the new language, because SSR translations contain only the routes
      // object, to decrease initial doc size.
      // Subsequent calls will be from cache
      await i18n.reloadResources([languageToChangeTo]);
      i18n.changeLanguage(languageToChangeTo);
      dayjs.locale(languageToChangeTo);

      const urlFromState = translatedUrlState[languageToChangeTo];

      navigateToRoute(infoFromCurrentPath.route, {
        newLanguage: languageToChangeTo,
        newCountry: countryToChangeTo,
        params: urlFromState ? [urlFromState] : infoFromCurrentPath.params,
        search: infoFromCurrentPath.search,
        ...(infoFromCurrentPath?.state ? { state: infoFromCurrentPath?.state } : {}),
      });

      if (shouldUpdateAccount) {
        await updateAccountLanguage(languageToChangeTo);
      }

      cookies.set(COOKIE_KEY_PREFERRED_LANGUAGE, languageToChangeTo, { path: '/' });
      cookies.set(COOKIE_KEY_PREFERRED_COUNTRY, countryToChangeTo, { path: '/' });
    },
    [
      country,
      infoFromCurrentPath.params,
      infoFromCurrentPath.route,
      infoFromCurrentPath.search,
      infoFromCurrentPath?.state,
      language,
      navigateToRoute,
      translatedUrlState,
      updateAccountLanguage,
    ],
  );

  const changeCountryAndLanguage = useCallback(
    async (newLanguage: Language | undefined, newCountry: Country | undefined) => {
      if (newLanguage === language && newCountry === country) return;
      const languageToChangeTo = newLanguage || language;
      const countryToChangeTo = newCountry || country;

      await i18n.reloadResources([languageToChangeTo]);
      i18n.changeLanguage(languageToChangeTo);
      dayjs.locale(languageToChangeTo);

      cookies.set(COOKIE_KEY_PREFERRED_LANGUAGE, languageToChangeTo, { path: '/' });
      cookies.set(COOKIE_KEY_PREFERRED_COUNTRY, countryToChangeTo, { path: '/' });
    },
    [country, language],
  );

  return { changeLocale, changeCountryAndLanguage };
};
